import { Edit } from "@mui/icons-material";
import { IconButton, Switch, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Column, AbTest } from "src/api/types";
import { theme } from "src/assets/theme";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { useUpdateExperimentMutation } from "src/api/services/experimentsApi";

export const useColumns = (
  onEdit: (item: AbTest | null) => void,
): Column<AbTest>[] => {
  const [updateExperiment] = useUpdateExperimentMutation();

  const handleUpdate = useCallback(
    (row: AbTest) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateExperiment({
        is_active: event.target.checked,
        id: row.id,
      });
    },
    [updateExperiment],
  );

  return useMemo(
    () => [
      {
        id: "is_active",
        label: "Active",
        minWidth: 10,
        align: "center",
        orderable: false,
        format: (row) => row.is_active?.toString() ?? "No",
        render: (row) => (
          <Switch
            color="secondary"
            checked={row.is_active ?? false}
            onChange={handleUpdate(row)}
          />
        ),
      },
      {
        id: "id",
        label: "ID",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.id.toString(),
        render: (row) => <Link to={`${row.id}/variants`}>{row.id}</Link>,
      },
      {
        id: "name",
        label: "Name",
        minWidth: 70,
        align: "center",
        orderable: false,
        format: (row) => row.name,
        render: (row) => <Typography>{row.name}</Typography>,
      },
      {
        id: "product",
        label: "Products",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.products.map((p) => p.name).join(", "),
        render: (row) => (
          <Typography>{row.products.map((p) => p.name).join(", ")}</Typography>
        ),
      },
      {
        id: "description",
        label: "Description",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.description ?? "N/A",
        render: (row) => <Typography>{row.description ?? "N/A"}</Typography>,
      },
      {
        id: "edit",
        label: "Edit",
        minWidth: 80,
        align: "center",
        orderable: false,
        format: (row) => row.id.toString(),
        render: (row) => (
          <IconButton onClick={() => onEdit(row)}>
            <Edit sx={{ color: theme.palette.secondary.main }} />
          </IconButton>
        ),
      },
      {
        id: "results",
        label: "Results",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: () => "",
        render: (row) => (
          <IconButton component={Link} to={`${row.id}/results`}>
            <QueryStatsIcon sx={{ color: theme.palette.primary.main }} />
          </IconButton>
        ),
      },
    ],
    [handleUpdate, onEdit],
  );
};
